// Service Worker Registration Helper
// This module provides utilities for registering and managing the service worker

export class ServiceWorkerManager {
  private registration: ServiceWorkerRegistration | null = null;
  private updateAvailable = false;
  private onUpdateAvailable: (() => void) | null = null;

  async register(): Promise<void> {
    if (!('serviceWorker' in navigator)) {
      return;
    }

    try {
      const registration = await navigator.serviceWorker.register('/sw.js', {
        scope: '/'
      });

      this.registration = registration;

      // Check for updates
      registration.addEventListener('updatefound', () => {
        const newWorker = registration.installing;
        if (newWorker) {
          newWorker.addEventListener('statechange', () => {
            if (
              newWorker.state === 'installed' &&
              navigator.serviceWorker.controller
            ) {
              this.updateAvailable = true;
              this.notifyUpdateAvailable();
            }
          });
        }
      });

      // Check for updates when user returns to the page
      document.addEventListener('visibilitychange', () => {
        if (!document.hidden && registration) {
          registration.update();
        }
      });

      // Pre-cache Vite assets after registration (non-blocking)
      this.preCacheAssets().catch(err =>
        console.warn('[ServiceWorkerManager] Pre-cache failed:', err)
      );
    } catch (error) {
      console.error('[ServiceWorkerManager] Registration failed:', error);
    }
  }

  private async preCacheAssets(): Promise<void> {
    // Wait for the service worker to be ready
    const registration = await navigator.serviceWorker.ready;

    // Get all the assets from the current page
    const assets: string[] = [];

    // Add all script sources
    document.querySelectorAll('script[src]').forEach(script => {
      const src = (script as HTMLScriptElement).src;
      if (src && !src.includes('hot-update')) {
        assets.push(new URL(src).pathname);
      }
    });

    // Add all link stylesheets
    document.querySelectorAll('link[rel="stylesheet"]').forEach(link => {
      const href = (link as HTMLLinkElement).href;
      if (href) {
        assets.push(new URL(href).pathname);
      }
    });

    // Add common assets that might be dynamically imported

    if (assets.length > 0) {
      // Send message to service worker to cache these URLs
      if (registration.active) {
        registration.active.postMessage({
          type: 'CACHE_URLS',
          urls: assets
        });
      }
    }
  }

  private notifyUpdateAvailable(): void {
    if (this.onUpdateAvailable) {
      this.onUpdateAvailable();
    }
  }

  setUpdateCallback(callback: () => void): void {
    this.onUpdateAvailable = callback;
  }

  async skipWaitingAndReload(): Promise<void> {
    if (!this.registration?.waiting) return;

    // Tell the waiting service worker to skip waiting
    this.registration.waiting.postMessage({ type: 'SKIP_WAITING' });

    // Reload once the new service worker has taken control
    await new Promise<void>(resolve => {
      const onControllerChange = () => {
        navigator.serviceWorker.removeEventListener(
          'controllerchange',
          onControllerChange
        );
        resolve();
      };
      navigator.serviceWorker.addEventListener(
        'controllerchange',
        onControllerChange
      );
    });

    window.location.reload();
  }

  async clearCache(): Promise<void> {
    if (!navigator.serviceWorker.controller) return;

    navigator.serviceWorker.controller.postMessage({ type: 'CLEAR_CACHE' });
  }

  async unregister(): Promise<void> {
    if (!this.registration) return;

    await this.registration.unregister();
  }

  isUpdateAvailable(): boolean {
    return this.updateAvailable;
  }

  getRegistration(): ServiceWorkerRegistration | null {
    return this.registration;
  }
}

// Create a singleton instance
export const swManager = new ServiceWorkerManager();

// Note: Service worker registration is handled explicitly in main.tsx
// No auto-registration on import to prevent double registration
